import { useEffect, useState, useRef } from "react";
import { useGameState } from "./game/GameState";
import { getDebugParams } from "@/lib/debugUtils";
import { streakManager } from "./game/managers/StreakManager";
import { StreakDisplay } from "./completion/StreakDisplay";
import { ResultsDisplay } from "./completion/ResultsDisplay";
import { GuessHistory } from "./GuessHistory";
import { CompletionHeader } from "./completion/CompletionHeader";
import { calculateTotalScore } from "@/utils/scoreCalculator";
import { GameStatus } from "@/lib/types/GameTypes";
import * as CookieManager from "@/lib/managers/CookieManager";
import { handleNewDay, getEffectiveDate, formatDate } from "@/lib/dateUtils";
import { CompletionStateManager } from "./game/managers/CompletionStateManager";
import { getCompletionMessage, getEffectiveGameStatus } from "./game/managers/GameStatusManager";
import { Link } from "react-router-dom";
import { RefreshCw, Trophy, Dices } from "lucide-react";
import { AnimatedRankCard } from "./ranks/AnimatedRankCard";
import { animationCoordinator } from "@/lib/managers/AnimationCoordinator"; 
import { useGameMode } from "@/contexts/GameModeContext";
import { Button } from "./ui/button";
import { trackEvent } from "@/lib/analytics";
import { completeGameSession, resetGameSession } from "@/lib/playerTracking";
import { resetGame } from "@/lib/gameReset";
import { toast } from "sonner";
import { WordSelectionStrategyFactory } from "@/lib/wordSelection/StrategyFactory";
import { GamePromoPanel } from "./completion/GamePromoPanel";
import { Header } from "./layout/Header";
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';
import { getWordForDay } from "@/lib/wordLibrary";
import { ScrollArea } from "./ui/scroll-area";
import { useViewMode } from "@/contexts/ViewModeContext";
interface GameCompletionScreenProps {
  levelAttempts: number[];
  isOpen: boolean;
  onClose: () => void;
  failedAtLevel?: number;
  guessHistory: {
    level: number;
    word: string;
    guesses: { letter: string; status: string }[][];
  }[];
  immediate?: boolean;
}

const GameCompletionScreen = ({ 
  levelAttempts: propLevelAttempts, 
  isOpen,
  onClose,
  failedAtLevel: propFailedAtLevel,
  guessHistory: propGuessHistory,
  immediate = false,
}: GameCompletionScreenProps) => {
  const { currentMode, setCurrentMode, switchToMode } = useGameMode();
  const { isLivestream } = useViewMode();
  const { gameStatus: stateGameStatus } = useGameState();
  const [streakState, setStreakState] = useState(streakManager.getStreakState());
  const [previousStreak, setPreviousStreak] = useState<number | undefined>(undefined);
  const [isStreakReady, setIsStreakReady] = useState(false);
  const [showGuessHistory, setShowGuessHistory] = useState(false);
  const [isNewDay, setIsNewDay] = useState(false);
  const [showRankCard, setShowRankCard] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const hasInitialized = useRef(false);
  const [isVisible, setIsVisible] = useState(immediate);
  const screenReadyRef = useRef(immediate);
  const pendingAnimationsRef = useRef(0);
  const lastModeRef = useRef(currentMode);
  const initialMountRef = useRef(true);
  const visibilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Consolidated visibility and completion state management
  useEffect(() => {
    console.log('GameCompletionScreen: Consolidated effect triggered', {
      isOpen,
      currentMode,
      immediate,
      isVisible,
      timestamp: new Date().toISOString()
    });

    // Clear any pending timeouts
    if (visibilityTimeoutRef.current) {
      clearTimeout(visibilityTimeoutRef.current);
      visibilityTimeoutRef.current = null;
    }

    // Handle immediate mode or explicit isOpen
    if (isOpen && !isVisible) {
      if (immediate) {
        console.log('GameCompletionScreen: Setting immediate visibility');
        setIsVisible(true);
        screenReadyRef.current = true;
        initialMountRef.current = false;
        return;
      }

      // For practice mode, use shorter delays to reduce flicker
      const delay = currentMode === 'practice' ? 50 : 100;
      
      visibilityTimeoutRef.current = setTimeout(() => {
        setIsVisible(true);
        screenReadyRef.current = true;
        initialMountRef.current = false;
      }, delay);
      
      hasInitialized.current = true;
    } else if (!isOpen && isVisible) {
      setIsVisible(false);
    } else if (!isOpen) {
      // Check completion state when not explicitly opened (date-aware via CompletionStateManager)
      const shouldShowCompletion = CompletionStateManager.shouldShowCompletion(currentMode);
      
      if (shouldShowCompletion && !isVisible) {
        console.log('GameCompletionScreen: Setting visibility based on completion state', {
          from: 'CompletionStateManager',
          currentMode
        });
        
        // For practice mode, show immediately to reduce flicker
        if (currentMode === 'practice') {
          setIsVisible(true);
          screenReadyRef.current = true;
        } else {
          visibilityTimeoutRef.current = setTimeout(() => {
            setIsVisible(true);
            screenReadyRef.current = true;
          }, 50);
        }
      }
    }

    // Cleanup function
    return () => {
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
        visibilityTimeoutRef.current = null;
      }
    };
  }, [isOpen, currentMode, immediate, isVisible]);

  // Mode switch handler (simplified)
  useEffect(() => {
    const handleModeSwitch = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { targetMode } = customEvent.detail;
      
      console.log('Mode switch detected in GameCompletionScreen', {
        to: targetMode,
        timestamp: new Date().toISOString()
      });
      
      lastModeRef.current = targetMode;
      
      // For practice mode, minimize hiding to reduce flicker
      if (targetMode !== 'practice') {
        setIsVisible(false);
      }
    };
    
    window.addEventListener('babblegram_mode_switched', handleModeSwitch);
    
    return () => {
      window.removeEventListener('babblegram_mode_switched', handleModeSwitch);
    };
  }, []);

  useEffect(() => {
    const initializeStreak = () => {
      console.log('Initializing streak state for mode:', currentMode);
      
      // Use mode-aware streak manager instead of direct cookie access
      const freshState = streakManager.getStreakState();
      
      console.log('🔍 COMPLETION SCREEN - Fresh streak state from streakManager:', {
        freshState,
        currentMode,
        timestamp: new Date().toISOString()
      });
      setStreakState(freshState);
      
      // Use mode-aware storage for previous streak
      const savedPreviousStreakValue = modeAwareStorage.getCookie('previous_streak');
      const savedPreviousStreak = savedPreviousStreakValue ? parseInt(savedPreviousStreakValue) : null;
      setPreviousStreak(savedPreviousStreak !== null ? savedPreviousStreak : freshState.currentStreak);
      
      setIsStreakReady(true);
    };

    initializeStreak();

    console.log('Setting up streak subscription for mode:', currentMode);
    const unsubscribe = streakManager.subscribeToStreakUpdates((newStreak: number) => {
      console.log('Streak update received:', {
        newStreak,
        previousStreak: streakState.currentStreak,
        currentMode,
        timestamp: new Date().toISOString()
      });
      
      setPreviousStreak(streakState.currentStreak);
      
      // Use mode-aware streak manager for updates too
      const newState = streakManager.getStreakState();
      setStreakState(newState);
    });
    
    return () => {
      console.log('Cleaning up streak subscription');
      unsubscribe();
    };
  }, [currentMode]); // Add currentMode as dependency to refresh when mode changes

  useEffect(() => {
    const checkNewDay = async () => {
      const { isNewDay: newDay } = await handleNewDay();
      setIsNewDay(newDay);
    };
    checkNewDay();
  }, []);

  const effectiveGameStatus: GameStatus = getEffectiveGameStatus();

  const savedLevelAttempts = modeAwareStorage.getLevelAttempts();
  const savedGuessHistory = modeAwareStorage.getGuessHistory();
  const savedFailedLevel = modeAwareStorage.getFailedLevel();
  // Use mode-aware storage for all game state access
  const savedDate = modeAwareStorage.getCookie('game_persistence_date');
  const savedCurrentLevel = modeAwareStorage.getCurrentLevel();
  const today = formatDate(getEffectiveDate());
  
  // Remove cross-mode completion state - each mode should be independent
  
  const isNewDailyDay = currentMode === 'daily' && savedDate !== today;
  const shouldUseSavedState = !isNewDailyDay && savedDate === today;

  const effectiveLevelAttempts = isNewDailyDay
    ? []
    : (shouldUseSavedState && savedLevelAttempts ? savedLevelAttempts : propLevelAttempts);
    
  const unfilteredGuessHistory = isNewDailyDay
    ? []
    : (shouldUseSavedState && savedGuessHistory ? savedGuessHistory : propGuessHistory);
  
  const dailyWords = getWordForDay(getEffectiveDate());
  const effectiveGuessHistory = Array.isArray(unfilteredGuessHistory)
    ? unfilteredGuessHistory.filter(h => dailyWords[h.level - 1] === h.word)
    : [];
    
  const rawFailedLevel = isNewDailyDay
    ? undefined
    : (shouldUseSavedState && effectiveGameStatus === ('lost' as GameStatus)
      ? (savedFailedLevel ? savedFailedLevel : propFailedAtLevel)
      : propFailedAtLevel);

    
  const effectiveFailedLevel = rawFailedLevel !== undefined && 
                               !isNaN(Number(rawFailedLevel)) && 
                               Number(rawFailedLevel) > 0 ? 
                               Number(rawFailedLevel) : undefined;
                               
  console.log("GameCompletionScreen: Validated failed level:", {
    rawFailedLevel,
    effectiveFailedLevel,
    savedFailedLevel,
    propFailedAtLevel,
    typeOfRaw: typeof rawFailedLevel,
    isNaN: rawFailedLevel !== undefined && isNaN(Number(rawFailedLevel))
  });

  useEffect(() => {
    const score = calculateTotalScore(effectiveGuessHistory, effectiveFailedLevel);
    console.log("Score calculation check:", {
      score,
      effectiveFailedLevel,
      effectiveGuessHistory: JSON.stringify(effectiveGuessHistory),
      effectiveGameStatus
    });
  }, [effectiveGuessHistory, effectiveFailedLevel, effectiveGameStatus]);

  const showDebugLogs = getDebugParams().showLogs;

  const isLevel4Complete = 
    (savedCurrentLevel === 4 && effectiveGameStatus === 'won') || 
    effectiveGuessHistory.some(h => 
      h.level === 4 && 
      h.guesses.some(g => g.every(l => l.status === 'correct')) &&
      effectiveGameStatus === 'won'
    );

  const completionTrackedRef = useRef<string | null>(null);
  const shouldShowCompletion = isOpen || CompletionStateManager.shouldShowCompletion(currentMode);

  // Removed redundant visibility check effects - now handled in consolidated effect above


  // Fire a single completion event per game result
  useEffect(() => {
    if (!shouldShowCompletion) return;
    const key = `${currentMode}-${effectiveGameStatus}-${effectiveGuessHistory.length}`;
    if (completionTrackedRef.current === key) return;
    completionTrackedRef.current = key;
    const finalScore = calculateTotalScore(effectiveGuessHistory, effectiveFailedLevel);
    trackEvent('game_complete', {
      mode: currentMode,
      outcome: effectiveGameStatus ?? 'unknown',
      score: finalScore,
      failed_level: effectiveFailedLevel ?? 0,
      streak: streakState.currentStreak
    });
    if (effectiveGameStatus === 'won' || effectiveGameStatus === 'lost') {
      completeGameSession(currentMode as 'daily' | 'practice' | 'custom', effectiveGameStatus, {
        maxLevel: effectiveGameStatus === 'won' ? 4 : (effectiveFailedLevel ?? undefined),
        score: finalScore
      });
    }
  }, [shouldShowCompletion, currentMode, effectiveGameStatus, effectiveGuessHistory, effectiveFailedLevel, streakState.currentStreak]);


  if (!shouldShowCompletion) {
    return null;
  }

  const score = calculateTotalScore(effectiveGuessHistory, effectiveFailedLevel);

  const handleCloseRankCard = () => {
    setShowRankCard(false);
  };
  
  const handleOpenRankCard = () => {
    trackEvent('open_rank_card', { mode: currentMode, streak: streakState.currentStreak });
    setShowRankCard(true);
  };
  
  const handlePlayAgain = async () => {
    trackEvent('play_again', { mode: currentMode });
    resetGameSession(currentMode as 'daily' | 'practice' | 'custom');
    completionTrackedRef.current = null;
    try {
      setIsTransitioning(true);
      
      // Set play again flag to force new sequence generation in practice mode
      localStorage.setItem('babblegram_practice_mode_play_again', 'true');
      console.log('Play Again button pressed, setting flag for new sequence generation', {
        mode: currentMode,
        timestamp: new Date().toISOString()
      });
      
      // Reset the strategy to force new sequence
      WordSelectionStrategyFactory.resetStrategy(currentMode);
      
      await resetGame(true);
      
      onClose();
      
      // No page reload needed anymore
      setIsTransitioning(false);
      
    } catch (error) {
      setIsTransitioning(false);
      console.error('Error starting new game:', error);
      toast.error("Failed to start a new game. Please try again.");
    }
  };

  const handlePlayPracticeMode = async () => {
    trackEvent('mode_switch', { from_mode: 'daily', to_mode: 'practice', source: 'completion_screen' });
    console.log('🔄 Starting mode switch from daily to practice');
    
    try {
      setIsTransitioning(true);
      
      // Use the standardized switchToMode function with preserveTargetState: true
      // This will preserve any existing practice completion state
      const success = await switchToMode('practice', true);
      
      if (!success) {
        throw new Error('Failed to switch to practice mode');
      }
      
      console.log('✅ Successfully switched to practice mode');
      setIsTransitioning(false);
      
    } catch (error) {
      setIsTransitioning(false);
      console.error('Error switching to practice mode:', error);
      toast.error("Failed to switch to practice mode. Please try again.");
    }
  };

  const isPracticeMode = currentMode === 'practice';
  const isDailyMode = currentMode === 'daily';

  return (
    <>
      <div
        className={`${isLivestream ? 'livestream-completion-shell' : 'fixed inset-0'} flex items-center justify-center z-[9999]`}
        style={{ overflow: 'hidden', pointerEvents: isVisible ? 'auto' : 'none' }}
      >
        <div className={`${isLivestream ? 'absolute' : 'fixed'} inset-0 bg-black/80 ${isVisible ? 'opacity-100' : 'opacity-0'}`} />
      
      <div
        className={`game-completion-screen relative w-full h-full bg-gray-900 shadow-xl flex flex-col ${isLivestream ? 'livestream-completion-screen' : ''} ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        <ScrollArea className="flex-1 min-h-0">
          <div className={`flex flex-col justify-start max-w-xl mx-auto ${isLivestream ? 'livestream-completion-content min-w-0 px-4 pb-5 pt-3' : 'min-w-[380px] px-6 pb-6 pt-6 mt-8'}`}>
            <CompletionHeader getCompletionMessage={getCompletionMessage} isPracticeMode={isPracticeMode} />
            <div className="stats-area" style={{ display: showGuessHistory ? 'none' : 'block' }}>
              {isStreakReady && (
                <StreakDisplay 
                  currentStreak={streakState.currentStreak}
                  previousStreak={previousStreak}
                  savedGameStatus={effectiveGameStatus}
                  onStreakCardOpen={handleOpenRankCard}
                  isPracticeMode={isPracticeMode}
                />
              )}

              <ResultsDisplay 
                effectiveLevelAttempts={effectiveLevelAttempts}
                effectiveGuessHistory={effectiveGuessHistory}
                effectiveFailedLevel={effectiveFailedLevel}
                currentStreak={streakState.currentStreak}
                previousStreak={previousStreak}
                score={score}
                showDebugLogs={showDebugLogs}
                savedGameStatus={effectiveGameStatus}
                isPracticeMode={isPracticeMode}
                isLivestream={isLivestream}
                onToggleGuessHistory={() => setShowGuessHistory(!showGuessHistory)}
                showGuessHistory={showGuessHistory}
              />
              
              <div className="mt-5 flex flex-col items-center gap-3">
                {isPracticeMode && !isTransitioning && (
                  <>
                    <Button 
                      onClick={handlePlayAgain}
                      className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2 w-full max-w-xs"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Play Again
                    </Button>
                    <Button 
                      onClick={() => {
                        trackEvent('mode_switch', { from_mode: 'practice', to_mode: 'daily', source: 'completion_screen' });
                        switchToMode('daily', true);
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 w-full max-w-xs"
                    >
                      <Dices className="h-4 w-4" />
                      Play Daily Challenge
                    </Button>
                  </>
                )}
                
                {isDailyMode && !isTransitioning && (
                  <>
                    <Button 
                      onClick={handlePlayPracticeMode}
                      className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2 w-full max-w-xs"
                    >
                      <Dices className="h-4 w-4" />
                      Play Practice Mode
                    </Button>
                    
                    {/* Temporary debug button for cookie inspection 
                    <Button 
                      onClick={() => {
                        const gameRelatedCookies: { name: string; value: string }[] = [];
                        
                        // Get all cookies from document.cookie
                        if (document.cookie) {
                          const cookies = document.cookie.split(';');
                          cookies.forEach(cookie => {
                            const [name, value] = cookie.trim().split('=');
                            // Check for any game-related patterns (comprehensive list)
                            if (name && (
                              name.startsWith('babblegram_') ||
                              name.startsWith('babblegram_daily_') ||
                              name.startsWith('babblegram_practice_') ||
                              name.includes('game_') ||
                              name.includes('completion_') ||
                              name.includes('instruction_') ||
                              name.includes('streak') ||
                              name.includes('level') ||
                              name.includes('guess') ||
                              name.includes('keyboard') ||
                              name.includes('word') ||
                              name.includes('redirect_to_welcome') ||
                              name.includes('from_welcome_page') ||
                              name.includes('shared_sequence')
                            )) {
                              gameRelatedCookies.push({ name, value: value || '' });
                            }
                          });
                        }
                        
                        // Also check localStorage for fallback cookies
                        const fallbackCookies: { name: string; value: string }[] = [];
                        try {
                          for (let i = 0; i < localStorage.length; i++) {
                            const key = localStorage.key(i);
                            if (key && key.startsWith('cookie_fallback_')) {
                              const value = localStorage.getItem(key);
                              fallbackCookies.push({ name: key, value: value || '' });
                            }
                          }
                        } catch (error) {
                          console.log('Could not access localStorage');
                        }
                        
                        // Format the output
                        const output = {
                          timestamp: new Date().toISOString(),
                          mode: 'daily',
                          cookies: gameRelatedCookies,
                          fallbackCookies,
                          totalCount: gameRelatedCookies.length + fallbackCookies.length
                        };
                        
                        // Copy to clipboard
                        navigator.clipboard.writeText(JSON.stringify(output, null, 2))
                          .then(() => {
                            toast.success('All game cookies copied to clipboard!');
                            console.log('🍪 COOKIE DEBUG - All game cookies:', output);
                          })
                          .catch(() => {
                            toast.error('Failed to copy cookies');
                          });
                      }}
                      variant="outline"
                      className="bg-orange-600/20 border-orange-600 text-orange-300 hover:bg-orange-600/30 text-xs w-full max-w-xs"
                    >
                      🍪 Copy All Game Cookies (Debug)
                </Button>*/}
                  </>
                )}
                
                {isTransitioning && (
                  <div className="text-center py-2">
                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-purple-500"></div>
                    <span className="ml-2 text-sm text-gray-300">Switching mode...</span>
                  </div>
                )}
              </div>
            </div>

            <GuessHistory 
              guessHistory={effectiveGuessHistory} 
              failedAtLevel={effectiveFailedLevel}
              showGuessHistory={showGuessHistory}
              onClose={() => setShowGuessHistory(false)}
            />

            {!isLivestream && <GamePromoPanel />}
          </div>
        </ScrollArea>
      </div>
      
      <AnimatedRankCard 
        streak={streakState.currentStreak || 0} 
        isVisible={showRankCard} 
        onClose={handleCloseRankCard} 
        isLivestream={isLivestream}
      />
      </div>
    </>
  );
};

export default GameCompletionScreen;
